// Recognize who an event is for from its title/description, so events created
// on any phone with the normal Google Calendar app still get the right person
// colors. Deliberately conservative: only explicit tag forms match, never a bare
// name inside a sentence ("Noah's Ark" is not tagged Noah).
//
//   "Ethan: Dentist"          prefix with colon
//   "Ethan & Noah: Swim"      several people, & / and / ,
//   "Ethan - Dentist"         prefix with dash
//   "Dentist #ethan"          hashtag anywhere (also in the description)
//   "Dentist @Ethan"          mention anywhere
//   "Dentist (Ethan)"         parenthesised name
//   "Kids: Pajama day"        every child

export interface PersonRef {
  id: string;
  displayName: string;
  isChild: boolean;
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function nameKey(s: string) {
  return s.trim().toLowerCase();
}

/** Returns matched member ids and the title with recognized tags removed. */
export function parsePeopleTags(title: string, description: string | null, members: PersonRef[]): { ids: string[]; title: string } {
  const byName = new Map(members.map((m) => [nameKey(m.displayName), m]));
  const kids = members.filter((m) => m.isChild);
  const found = new Set<string>();
  let clean = title;

  const resolve = (token: string): PersonRef[] | null => {
    const k = nameKey(token);
    if (k === "kids" || k === "children") return kids.length ? kids : null;
    const m = byName.get(k);
    return m ? [m] : null;
  };

  // Prefix: "A: ...", "A & B: ...", "A, B and C - ..."
  const prefix = /^\s*([\p{L}][\p{L}'’ .-]{0,40}?(?:\s*(?:,|&|\+|\band\b)\s*[\p{L}][\p{L}'’ .-]{0,40}?)*)\s*(?::|\s[-–—]\s)\s*/u.exec(clean);
  if (prefix) {
    const tokens = prefix[1]!.split(/\s*(?:,|&|\+|\band\b)\s*/u).filter(Boolean);
    const matched = tokens.map(resolve);
    // Only accept the prefix if every token is a known person (avoids "Re: budget").
    if (tokens.length > 0 && matched.every(Boolean)) {
      matched.flat().forEach((m) => found.add(m!.id));
      clean = clean.slice(prefix[0].length);
    }
  }

  // Hashtags, mentions and parenthesised names, in title and description.
  for (const m of members) {
    const n = escape(m.displayName);
    const re = new RegExp(`(?:#|@)${n}\\b|\\(\\s*${n}\\s*\\)`, "iu");
    if (re.test(clean) || (description && re.test(description))) {
      found.add(m.id);
      clean = clean.replace(new RegExp(`\\s*(?:(?:#|@)${n}\\b|\\(\\s*${n}\\s*\\))`, "giu"), "");
    }
  }
  if (/(?:#|@)kids\b|\(\s*kids\s*\)/iu.test(clean) || (description && /(?:#|@)kids\b/iu.test(description))) {
    kids.forEach((k) => found.add(k.id));
    clean = clean.replace(/\s*(?:(?:#|@)kids\b|\(\s*kids\s*\))/giu, "");
  }

  clean = clean.trim();
  return { ids: members.filter((m) => found.has(m.id)).map((m) => m.id), title: clean || title.trim() };
}

/** Stable key used to tag recurring events by series. */
export function eventKey(e: { id: string; recurringEventId: string | null }): string {
  return e.recurringEventId ?? e.id;
}

/**
 * Who appears on the chore chart and balances: every child, plus any adult who
 * has a chore assigned (adults can take chores too). Children come first.
 */
export function choreMembers<M extends { id: string; isChild: boolean }>(members: M[], assigneeIds: Iterable<string | null>): M[] {
  const assigned = new Set(assigneeIds);
  const kids = members.filter((m) => m.isChild);
  const adults = members.filter((m) => !m.isChild && assigned.has(m.id));
  const people = [...kids, ...adults];
  return people.length ? people : members;
}

/** Children first, then everyone else: the order for picking whose chore it is. */
export function kidsFirst<M extends { isChild: boolean }>(members: M[]): M[] {
  return [...members.filter((m) => m.isChild), ...members.filter((m) => !m.isChild)];
}
